'use client';

/**
 * EditorialInvestSidebar — the editorial (`se3`) test version of the sticky
 * right-rail invest card rendered inside `BuilderClassicTwoColumnLayout`.
 *
 * This is a *presentational* component: every piece of data + every behaviour
 * (CTA text/colour resolution, the invest/reserve/waitlist click handlers, the
 * TTW / Reg D / over-raised visibility rules, the amount-raised exclusion
 * logic, the login-modal redirect) already lives in `BuilderClassicInvestSidebar`.
 * When the se3 reskin is active, that component computes its values exactly as
 * before and hands them here as plain props instead of rendering the classic
 * gradient card. So the editorial sidebar can never drift from the control
 * sidebar's business logic — only its skin differs.
 *
 * Matches the design handoff (07-campaign-atombeam.desktop / .mobile):
 *   - white card, rounded, drop shadow, GOLD top-accent hairline
 *   - large solid teal CTA ("Get Equity" / "Invest Now" / "Reserve Now" /
 *     "Join the Waitlist") with a "$x Per Share" subline
 *   - stat grid: gold tracked-uppercase labels (with the existing info-icon
 *     tooltips) over large IBM Plex Serif teal values, laid out as
 *     PREVIOUSLY CROWDFUNDED (full width) → RAISED / INVESTORS →
 *     MIN INVEST / VALUATION
 *   - a separate "Most Momentum / Most Funded" badge card below
 *   - a "Need help? Schedule a call" treatment slot (Reg D / Gold campaigns)
 *
 * Built from the se3 design-system tokens (`@startengine/design-system.se3.tokens`).
 */
import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { se3Colors, se3FontFamily } from '@startengine/design-system.se3.tokens';
import { imagePath } from 'utils/helpers';
// Use the handoff accent gold (#B58A4B) so the sidebar matches the offering body
// sections; se3Colors.gold (#8C6A2C) is a darker token that drifts from the design.
import { ACCENT_GOLD, TEAL } from './editorialSection';

const labelSx = {
  fontFamily: se3FontFamily.sans,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: ACCENT_GOLD,
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  lineHeight: 1.3,
};

const EDITORIAL_STAT_GRID_COLUMNS = 'minmax(0, 1.25fr) minmax(0, 0.75fr)';
const EDITORIAL_STAT_VALUE_OVERFLOW_WRAP = 'anywhere';
const BADGE_HELP_URL =
  'https://help.startengine.com/what-can-startengine-do-to-market-my-raise-ry4s6uRMK';

const valueSx = {
  fontFamily: se3FontFamily.serif,
  fontWeight: 500,
  // PD-36055 (Hannah): match the handoff — smaller, lighter stat values with
  // more breathing room (was 28/30px, too large/heavy vs the SambaNova design).
  fontSize: { xs: '21px', sm: '22px' },
  lineHeight: 1.2,
  color: TEAL,
  marginTop: '8px',
  maxWidth: '100%',
  minWidth: 0,
  overflowWrap: EDITORIAL_STAT_VALUE_OVERFLOW_WRAP,
  whiteSpace: 'normal',
  wordBreak: 'normal',
};

const tooltipSlotProps = {
  tooltip: {
    sx: {
      backgroundColor: se3Colors.night,
      color: se3Colors.onDark,
      fontSize: '12px',
      borderRadius: '10px',
      padding: '10px 14px',
      maxWidth: '240px',
      lineHeight: 1.5,
      fontFamily: se3FontFamily.sans,
    },
  },
  arrow: { sx: { color: se3Colors.night } },
};

const StatLabel = ({ children, tooltip, dataCy, color = ACCENT_GOLD }) => (
  <Box component="span" sx={{ ...labelSx, color }} data-cy={dataCy}>
    {children}
    {tooltip && (
      <Tooltip title={tooltip} arrow placement="top" slotProps={tooltipSlotProps}>
        <InfoOutlinedIcon sx={{ fontSize: 14, color, cursor: 'pointer' }} />
      </Tooltip>
    )}
  </Box>
);

StatLabel.propTypes = {
  children: PropTypes.node.isRequired,
  tooltip: PropTypes.node,
  dataCy: PropTypes.string,
  color: PropTypes.string,
};

const Stat = ({ label, value, tooltip, dataCy, valueDataCy, labelColor }) => (
  <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
    <StatLabel tooltip={tooltip} dataCy={dataCy} color={labelColor}>
      {label}
    </StatLabel>
    <Box sx={valueSx} data-cy={valueDataCy}>
      {value}
    </Box>
  </Box>
);

Stat.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node,
  tooltip: PropTypes.node,
  dataCy: PropTypes.string,
  valueDataCy: PropTypes.string,
  labelColor: PropTypes.string,
};

const EditorialInvestSidebar = ({
  ctaText,
  ctaSubline = null,
  ctaColor = TEAL,
  onCtaClick,
  ctaHref = '#',
  ctaDisabled = false,
  showCta = true,
  closed = false,
  stats = [],
  badges = [],
  scheduleCall = null,
  iraDisclaimer = null,
}) => (
  <Box data-cy="editorial-invest-sidebar" sx={{ fontFamily: se3FontFamily.sans, width: '100%' }}>
    <Box
      sx={{
        position: 'relative',
        backgroundColor: se3Colors.card,
        borderRadius: '16px',
        border: `1px solid ${se3Colors.edge}`,
        boxShadow: `inset 0 3px 0 ${ACCENT_GOLD}, 0 18px 40px -18px rgba(11,41,45,0.18), 0 4px 12px -6px rgba(11,41,45,0.10)`,
        padding: '24px',
        boxSizing: 'border-box',
      }}
    >
      {showCta && (
        <Box
          component="a"
          href={ctaHref}
          onClick={onCtaClick}
          data-cy="editorial-sidebar-cta"
          aria-disabled={ctaDisabled || undefined}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            textDecoration: 'none',
            backgroundColor: ctaColor,
            color: se3Colors.onDark,
            borderRadius: '14px',
            padding: '16px 20px',
            cursor: ctaDisabled ? 'default' : 'pointer',
            opacity: ctaDisabled ? 0.55 : 1,
            transition: 'opacity 0.2s ease',
            '&:hover': { opacity: ctaDisabled ? 0.55 : 0.92 },
          }}
        >
          <Box
            sx={{
              fontSize: '15px',
              fontWeight: 600,
              lineHeight: 1.2,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
            }}
          >
            {ctaText}
          </Box>
          {ctaSubline && (
            <Box
              sx={{ fontSize: '13px', fontWeight: 400, color: se3Colors.onDark2, marginTop: '4px' }}
            >
              {ctaSubline}
            </Box>
          )}
        </Box>
      )}

      {closed && (
        <Box
          data-cy="editorial-sidebar-closed"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            backgroundColor: se3Colors.gray2,
            color: se3Colors.onDark,
            borderRadius: '14px',
            padding: '16px 20px',
            fontSize: '17px',
            fontWeight: 600,
          }}
        >
          Offering Closed
        </Box>
      )}

      {iraDisclaimer}

      {stats.length > 0 && (
        <Box
          data-cy="editorial-sidebar-stats-grid"
          sx={{
            marginTop: showCta || closed ? '24px' : 0,
            display: 'grid',
            gridTemplateColumns: EDITORIAL_STAT_GRID_COLUMNS,
            columnGap: { xs: '16px', sm: '20px' },
            rowGap: '22px',
          }}
        >
          {stats.map((stat) => (
            <Box key={stat.key} sx={{ gridColumn: stat.fullWidth ? '1 / -1' : 'auto' }}>
              <Stat
                label={stat.label}
                value={stat.value}
                tooltip={stat.tooltip}
                dataCy={stat.labelDataCy}
                valueDataCy={stat.valueDataCy}
                labelColor={stat.labelColor}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>

    {badges.length > 0 && (
      <Box
        data-cy="editorial-sidebar-badges"
        sx={{
          marginTop: '16px',
          backgroundColor: se3Colors.card,
          border: `1px solid ${se3Colors.edge}`,
          borderRadius: '20px',
          padding: { xs: '20px', sm: '22px 24px' },
        }}
      >
        {badges.map((badge, index) => {
          const isMomentum = badge.title === 'Most Momentum';
          const iconFile = isMomentum ? 'criteria-badge-momentum' : 'criteria-badge-funded';

          return (
            <Box
              key={badge.title}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                paddingBottom: index < badges.length - 1 ? '16px' : 0,
                marginBottom: index < badges.length - 1 ? '16px' : 0,
                borderBottom: index < badges.length - 1 ? `1px dashed ${se3Colors.edge}` : 'none',
              }}
            >
              <Box
                component="img"
                src={`${imagePath}/images/offering/${iconFile}.png`}
                alt=""
                data-cy={`editorial-sidebar-badge-icon-${isMomentum ? 'momentum' : 'funded'}`}
                sx={{ width: 56, height: 56, objectFit: 'contain', flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    fontFamily: se3FontFamily.sans,
                    fontSize: '20px',
                    fontWeight: 700,
                    color: TEAL,
                    lineHeight: 1.2,
                  }}
                >
                  {badge.title}
                </Box>
                <Box
                  sx={{
                    fontFamily: se3FontFamily.sans,
                    fontSize: '16px',
                    color: se3Colors.ink3,
                    lineHeight: 1.35,
                    marginTop: '3px',
                  }}
                >
                  {badge.description}
                </Box>
              </Box>
            </Box>
          );
        })}
        {badges.some((badge) => badge.showDisclaimer) && (
          <Box
            sx={{
              borderTop: `1px dashed ${se3Colors.edge}`,
              marginTop: '18px',
              paddingTop: '18px',
              textAlign: 'center',
              fontFamily: se3FontFamily.sans,
              fontSize: '14px',
              color: se3Colors.ink3,
              lineHeight: 1.4,
            }}
          >
            What does this badge mean?{' '}
            <Box
              component="a"
              href={BADGE_HELP_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: TEAL,
                fontWeight: 700,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              See here
            </Box>
          </Box>
        )}
      </Box>
    )}

    {scheduleCall}
  </Box>
);

EditorialInvestSidebar.propTypes = {
  ctaText: PropTypes.node.isRequired,
  ctaSubline: PropTypes.node,
  ctaColor: PropTypes.string,
  onCtaClick: PropTypes.func,
  ctaHref: PropTypes.string,
  ctaDisabled: PropTypes.bool,
  showCta: PropTypes.bool,
  closed: PropTypes.bool,
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      value: PropTypes.node,
      tooltip: PropTypes.node,
      fullWidth: PropTypes.bool,
      labelDataCy: PropTypes.string,
      valueDataCy: PropTypes.string,
    }),
  ),
  badges: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      showDisclaimer: PropTypes.bool,
    }),
  ),
  scheduleCall: PropTypes.node,
  iraDisclaimer: PropTypes.node,
};

export default EditorialInvestSidebar;
